import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import "./home.css";
import SEOHead from "../components/SEOHead";
import fieldsWebP from "../assets/home/paneles.webp";
import fieldsJPG from "../assets/home/paneles-opt.jpg";
import energyWebP from "../assets/home/baterias-opt.webp";
import energyPNG from "../assets/home/baterias 2.png";
import heroWebP from "../assets/home/hero.webp";
import heroJPG from "../assets/home/hero.jpg";
import ConsultaLaptopWebP from "../assets/images/geotrim-home-tab.webp";
import ConsultaLaptopJPG from "../assets/images/geotrim-home-tab-opt.jpg";
import CalculadoraLaptopWebP from "../assets/images/calc-home-tab.webp";
import CalculadoraLaptopJPG from "../assets/images/calc-home-tab-opt.jpg";
import EcotrimHomeTabWebP from "../assets/images/ecotrim-home-tab.webp";
import EcotrimHomeTabJPG from "../assets/images/ecotrim-home-tab-opt.jpg";
import { Link } from "react-router-dom";
import DemoRequestButton from "../components/LandingComponents/DemoRequestButton";

const VideoCarousel = lazy(() => import("../components/LandingComponents/VideoCarousel"));

export default function HomePage() {
  const [featuresPanelTab, setFeaturesPanelTab] = useState("ecotrim");
  const [showVideoCarousel, setShowVideoCarousel] = useState(false);
  const carouselRef = useRef(null);

  useEffect(() => {
    if (!carouselRef.current || showVideoCarousel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setShowVideoCarousel(true);
          observer.disconnect();
        }
      },
      { rootMargin: "0px 0px" }
    );

    observer.observe(carouselRef.current);
    return () => observer.disconnect();
  }, [showVideoCarousel]);

  const featuresPanelContentByTab = {
    calculadora: {
      imageWebP: CalculadoraLaptopWebP,
      image: CalculadoraLaptopJPG,
      alt: "Calculadora gratuita para autoconsumos",
      title: "Calculadora gratuita para autoconsumos",
      bullets: [
        "Estima el nivel de ahorro y retorno de añadir una batería",
        "Algoritmos de cálculo avanzados",
        "Respuesta en segundos",
        "Informe completo detallado para tu empresa",
      ],
      cta: "Accede gratis",
      path: "/calc/",
    },
    ecotrim: {
      imageWebP: EcotrimHomeTabWebP,
      image: EcotrimHomeTabJPG,
      alt: "Trimergy Ecotrim para simulaciones detalladas",
      title: "Trimergy Ecotrim para simulaciones detalladas",
      bullets: [
        "Explora decenas de configuraciones",
        "Plataforma intuitiva",
        "Algoritmos de optimización contrastados",
        "Rendimiento operacional y financiero completo",
      ],
      cta: "Conoce Ecotrim",
      path: "/ecotrim/",
    },
    geotrim: {
      imageWebP: ConsultaLaptopWebP,
      image: ConsultaLaptopJPG,
      alt: "Desarrolla autoconsumo",
      title: "Desarrolla autoconsumo",
      bullets: [
        "Análisis geoespacial",
        "Catálogo de autoconsumos",
        "Mapa de capacidad en subestaciones y su evolución",
        "Reconocimiento por IA de autoconsumos",
        "Capas de infraestructuras próximas",
      ],
      cta: "Conoce Geotrim",
      path: "/geotrim/",
    },
    consulta: {
      imageWebP: ConsultaLaptopWebP,
      image: ConsultaLaptopJPG,
      alt: "Consulta Personalizada",
      title: "Consulta Personalizada",
      bullets: [
        "Asesoramiento a medida",
        "Evaluación in-situ",
        "Propuesta económica detallada",
        "Soporte técnico continuo",
      ],
      cta: "Contacto",
      path: "/contact/",
    },
  };

  const activeFeaturesPanel = featuresPanelContentByTab[featuresPanelTab];

  return (
    <div className="home-page">
      <SEOHead
        title="Almacenamiento energetico para empresas"
        description="Soluciones avanzadas de almacenamiento energetico con baterias para empresas. Simulacion, optimizacion economica y prospeccion geografica para instalaciones industriales."
        path="/"
      />
      <Helmet>
        <link rel="preload" as="image" href={heroWebP} type="image/webp" fetchpriority="high" />
      </Helmet>
      {/* Hero Section */}
      <section className="home-hero">
        <div className="hero-background" aria-hidden="true">
          <picture>
            <source srcSet={heroWebP} type="image/webp" />
            <img
              className="hero-background-img"
              src={heroJPG}
              alt=""
              fetchPriority="high"
              width="1920"
              height="1080"
            />
          </picture>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">
            Almacenamiento energético para empresas
          </h1>
          <p className="hero-subtitle">
            <strong>El uso eficiente de baterías genera ahorro, flexibilidad operativa y retorno financiero.</strong>
            <br />
            Desde simulación avanzada hasta{" "}
            <strong>prospección geográfica</strong>, Trimergy ofrece
            <br />
            herramientas para <strong>optimizar baterías </strong>
            en aplicaciones industriales
          </p>
          <Link className="hero-cta" to="/pricing/" discover={false}>
            Empezar
          </Link>
        </div>
      </section>

      {/* Features Section */}

      <section className="features-section" id="servicios">
        <div className="features-services-header">
          <h4 className="services-title">Usa nuestra tecnología con IA</h4>
          <p className="features-description">
            Identifica la mejor ubicacíon, {""}
            configura tu instalación y analiza miles de escenarios en segundos
          </p>
        </div>
        <div ref={carouselRef}>
          {showVideoCarousel ? (
            <Suspense fallback={null}>
              <VideoCarousel />
            </Suspense>
          ) : null}
        </div>
        <div className="features-cta-wrapper">
          <DemoRequestButton className="features-cta-btn" source="home_features">
            Pedir una demo
          </DemoRequestButton>
        </div>
      </section>

      {/* Features Section (duplicada) */}
      <section className="features-section features-section--products">
        <div className="features-centered-panel">
          <div
            className="features-centered-panel__header"
            role="tablist"
            aria-label="Producto"
          >
            <button
              type="button"
              role="tab"
              id="features-tab-ecotrim"
              aria-controls="features-panel-ecotrim"
              aria-selected={featuresPanelTab === "ecotrim"}
              className={
                featuresPanelTab === "ecotrim"
                  ? "features-tab features-tab--active features-tab--large"
                  : "features-tab features-tab--large"
              }
              onClick={() => setFeaturesPanelTab("ecotrim")}
            >
              Ecotrim
            </button>
            <button
              type="button"
              role="tab"
              id="features-tab-geotrim"
              aria-controls="features-panel-geotrim"
              aria-selected={featuresPanelTab === "geotrim"}
              className={
                featuresPanelTab === "geotrim"
                  ? "features-tab features-tab--active features-tab--large"
                  : "features-tab features-tab--large"
              }
              onClick={() => setFeaturesPanelTab("geotrim")}
            >
              Geotrim
            </button>
            <button
              type="button"
              role="tab"
              id="features-tab-calculadora"
              aria-controls="features-panel-calculadora"
              aria-selected={featuresPanelTab === "calculadora"}
              className={
                featuresPanelTab === "calculadora"
                  ? "features-tab features-tab--active features-tab--large"
                  : "features-tab features-tab--large"
              }
              onClick={() => setFeaturesPanelTab("calculadora")}
            >
              Calculadora
            </button>
          </div>

          <div className="features-centered-panel__content">
            <div
              role="tabpanel"
              id={`features-panel-${featuresPanelTab}`}
              aria-labelledby={`features-tab-${featuresPanelTab}`}
              className="features-panel-grid"
            >
              <div className="features-panel-text">
                <h3 className="features-panel-title">{activeFeaturesPanel.title}</h3>
                <ul className="features-panel-bullets">
                  {activeFeaturesPanel.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <Link
                  className="features-panel-cta"
                  to={activeFeaturesPanel.path}
                  discover={false}
                >
                  {activeFeaturesPanel.cta}
                </Link>
              </div>
              <div className="features-panel-image">
                <picture>
                  <source srcSet={activeFeaturesPanel.imageWebP} type="image/webp" />
                  <img
                    src={activeFeaturesPanel.image}
                    alt={activeFeaturesPanel.alt}
                    loading="lazy"
                    decoding="async"
                    width="800"
                    height="520"
                  />
                </picture>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="services-section-wth">
        <div className="services-wth-header">
          <h2 className="services-title">
            Trimergy optimiza tu inversión en baterías
          </h2>
        </div>

        <div className="services-wth-rows">
          <div className="services-wth-row services-wth-row--image-left">
            <picture>
              <source srcSet={fieldsWebP} type="image/webp" />
              <img
                src={fieldsJPG}
                alt="Dirigido a"
                className="services-wth-image"
                loading="lazy"
                decoding="async"
                width="800"
                height="600"
              />
            </picture>
            <div className="services-wth-text">
              <h3 className="services-wth-title">Dirigido a:</h3>
              <ul className="service-card-bullets">
                <li>Gestores de autoconsumos industriales</li>
                <li>Instaladores, EPCistas y desarrolladores</li>
                <li>Agencias inmobiliarias</li>
                <li>Inversores financieros</li>
                <li>Promotores centros de datos</li>
              </ul>
              <Link className="services-wth-more" to="/contact/" discover={false}>
                Consultar por perfil
              </Link>
            </div>
          </div>

          <div id="para-que" className="services-wth-row services-wth-row--image-right">
            <div className="services-wth-text">
              <h3 className="services-wth-title">Ventajas:</h3>
              <ul className="service-card-bullets">
                <li>Optimización económica y financiera</li>
                <li>Dimensionamiento para autoconsumos industriales</li>
                <li>Potencial máximo en cualquier ubicación</li>
                <li>
                  Explora terrenos, capacidad eléctrica, potencial autoconsumo y
                  núcleos de demanda
                </li>
              </ul>
              <Link className="services-wth-more" to="/contact/" discover={false}>
                Consultar sobre ventajas
              </Link>
            </div>
            <picture>
              <source srcSet={energyWebP} type="image/webp" />
              <img
                src={energyPNG}
                alt="Ventajas"
                className="services-wth-image"
                loading="lazy"
                decoding="async"
                width="800"
                height="600"
              />
            </picture>
          </div>
        </div>
      </section>

    </div>
  );
}
